import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MagazineSectionContainer from './MagazineSectionContainer';
import MultiCarousel from '../../components/carousel/MultiCarousel';
import ParticipantShowcaseCard from './ParticipantShowcaseCard';

/**
 * ParticipantsShowcaseSection - Final section showcasing participants
 * @param {object} props
 * @param {Array} props.participants - Array of participant user objects
 * @param {boolean} props.isMobile - Mobile responsive flag
 * @param {string} [props.title] - Section title (default: '참여 작가')
 * @param {React.ReactNode} [props.action] - Optional right-aligned action inside the section (e.g., View All button)
 */
const ParticipantsShowcaseSection = ({ participants = [], isMobile, title = '참여 작가', action = null }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const responsiveGap = useMemo(() => {
    if (isXs) return 8;
    if (isSm) return 12;
    if (isMd) return 16;
    return 20;
  }, [isXs, isSm, isMd]);

  const responsivePadding = useMemo(() => {
    if (isXs) return 3;
    if (isSm) return 4;
    if (isMd) return 6;
    return 8;
  }, [isXs, isSm, isMd]);

  const visibleCounts = useMemo(() => ({
    xs: 2,
    sm: 3,
    md: 3,
    lg: 5,
    xl: 5
  }), []);

  return (
    <MagazineSectionContainer
      title={title}
      titleAlign="left"
      isMobile={isMobile}
      sx={{ minWidth: 0, overflow: "hidden" }}
    >
      <Box sx={{
        minWidth: 0,
        width: "100%",
        maxWidth: !isMobile ? "100vw" : "calc(100vw - 16px)",
        py: responsivePadding,
        px: 0,
        boxSizing: 'border-box',
        contain: 'layout style',
      }}>
        <MultiCarousel
          items={participants}
          visible={visibleCounts}
          gap={responsiveGap}
          step={isXs ? 1 : isSm ? 2 : 3}
          renderItem={(user) => (
            <ParticipantShowcaseCard
              user={user}
              isMobile={isMobile}
              fallbackAvatarUrl={user.__fallbackAvatar}
            />
          )}
        />
      </Box>
      {action ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 0, pb: isMobile ? 2 : 3 }}>
          {action}
        </Box>
      ) : null}
    </MagazineSectionContainer>
  );
};

export default ParticipantsShowcaseSection;